
import { supabase } from "@/integrations/supabase/client";
import { Recipe } from "@/types/recipe";
import { withErrorHandling } from "../utils/errorHandling";

export interface RecipeUsageStats {
  recipeId: string;
  usageCount: number;
  lastUsedDate: string | null;
  daysSinceLastUse: number | null;
}

export interface RecipeWithStats extends Recipe {
  usageCount: number;
  lastUsedDate: string | null;
  daysSinceLastUse: number | null;
}

export const recipeStatsService = {
  /**
   * Get usage statistics for all recipes
   */
  getRecipeUsageStats: withErrorHandling(
    async (): Promise<RecipeUsageStats[]> => {
      // Get direct recipe usage and composition-based usage
      const { data: directUsage, error: directError } = await supabase
        .from("meal_plans")
        .select("recipe_id, date")
        .not("recipe_id", "is", null);

      if (directError) {
        throw directError;
      }

      // Get composition-based usage
      const { data: compositionUsage, error: compositionError } = await supabase
        .from("meal_plans")
        .select("date, meal_compositions!inner(recipe_ids)")
        .not("meal_composition_id", "is", null);

      if (compositionError) {
        throw compositionError;
      }

      // Combine all recipe usages
      const allUsages: Array<{ recipe_id: string; date: string }> = [];
      
      // Add direct usage
      directUsage.forEach((plan) => {
        if (plan.recipe_id) {
          allUsages.push({ recipe_id: plan.recipe_id, date: plan.date });
        }
      });
      
      // Add composition-based usage
      compositionUsage.forEach((plan: any) => {
        if (plan.meal_compositions?.recipe_ids) {
          plan.meal_compositions.recipe_ids.forEach((recipeId: string) => {
            allUsages.push({ recipe_id: recipeId, date: plan.date });
          });
        }
      });

      // Group by recipe_id and calculate stats
      const statsMap = new Map<string, { count: number; lastDate: string }>();
      
      allUsages.forEach((usage) => {
        const existing = statsMap.get(usage.recipe_id);
        if (!existing) {
          statsMap.set(usage.recipe_id, { count: 1, lastDate: usage.date });
        } else {
          existing.count++;
          if (usage.date > existing.lastDate) {
            existing.lastDate = usage.date;
          }
        }
      });

      // Convert to RecipeUsageStats array
      const now = new Date();
      return Array.from(statsMap.entries()).map(([recipeId, stats]) => {
        const lastUsedDate = new Date(stats.lastDate);
        const daysSinceLastUse = Math.floor((now.getTime() - lastUsedDate.getTime()) / (1000 * 60 * 60 * 24));
        
        return {
          recipeId,
          usageCount: stats.count,
          lastUsedDate: stats.lastDate,
          daysSinceLastUse,
        };
      });
    },
    "Error fetching recipe usage stats"
  ),

  /**
   * Combine recipes with their usage statistics
   */
  getRecipesWithStats: withErrorHandling(
    async (recipes: Recipe[]): Promise<RecipeWithStats[]> => {
      const usageStats = await recipeStatsService.getRecipeUsageStats();
      const statsMap = new Map(usageStats.map(stat => [stat.recipeId, stat]));

      return recipes.map(recipe => {
        const stats = statsMap.get(recipe.id);
        return {
          ...recipe,
          usageCount: stats?.usageCount || 0,
          lastUsedDate: stats?.lastUsedDate || null,
          daysSinceLastUse: stats?.daysSinceLastUse || null,
        };
      });
    },
    "Error combining recipes with stats"
  )
};
